import React, { useState, useEffect, useCallback } from "react";
import "./OtherChargeList.css";
import { otherChargeService } from "service/otherChargeService";
import { otherChargePaymentService } from "service/otherChargePaymentService";

const OtherChargeList = () => {
  const [chargeList, setChargeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buy-flow state (per-row quantity + which row is currently purchasing)
  const [quantities, setQuantities] = useState({});
  const [buyingId, setBuyingId] = useState(null);
  const [buyMessage, setBuyMessage] = useState(null);

  // Confirmation modal state
  const [confirmItem, setConfirmItem] = useState(null);

  const fetchCharges = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await otherChargeService.getAll();
      const list = Array.isArray(res?.data) ? res.data : [];
      setChargeList(list.map(otherChargeService.normalize));
    } catch (err) {
      console.error("Failed to load other service charges from API", err);
      setError(err.message || "Failed to load charges");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  const handleQtyChange = (id, value) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  };

  const getQty = (item) =>
    item.quantity_applicable ? Number(quantities[item.id] ?? item.quantity?.default ?? 1) : 1;

  // Buy button now just opens the confirmation modal
  const handleBuyClick = (item) => {
    setBuyMessage(null);
    setConfirmItem(item);
  };

  const closeConfirm = () => {
    if (buyingId) return; // don't allow closing mid-purchase
    setConfirmItem(null);
  };

  // Actual purchase call, fired from the modal's Confirm button
  const handleConfirmBuy = async () => {
    const item = confirmItem;
    if (!item) return;
    setBuyingId(item.id);
    setBuyMessage(null);
    try {
      const qty = item.quantity_applicable ? quantities[item.id] || item.quantity?.default || 1 : undefined;
      const res = await otherChargePaymentService.purchase(item.id, qty);
      setBuyMessage({ type: "success", text: res.message, id: item.id });
      setConfirmItem(null);
    } catch (err) {
      setBuyMessage({ type: "error", text: err.message || "Purchase failed", id: item.id });
      setConfirmItem(null);
    } finally {
      setBuyingId(null);
    }
  };

  const qty = confirmItem ? getQty(confirmItem) : 0;
  const unitPrice = confirmItem ? parseFloat(confirmItem.amount?.price) || 0 : 0;
  const gstPct = confirmItem ? parseFloat(confirmItem.amount?.gst) || 0 : 0;
  const subtotal = unitPrice * qty;
  const gstAmount = subtotal * (gstPct / 100);
  const total = subtotal + gstAmount;

  return (
    <div className="opay-charge-container mt-3">
      {/* Top Header Filter Bar */}
      <div className="opay-charge-header-bar mb-3">
        <div className="opay-charge-left">
          <i className="iconoir-home-alt opay-charge-home-icon"></i>
          <label className="opay-charge-breadcrumb-label ms-2">
            <input type="checkbox" className="opay-charge-checkbox" defaultChecked />
            <span>History</span>
          </label>
          <span className="opay-charge-arrow-separator">◽</span>
          <label className="opay-charge-breadcrumb-label">
            <input type="checkbox" className="opay-charge-checkbox" defaultChecked />
            <span>Other Services Charge List</span>
          </label>
          {loading && (
            <div className="spinner-border spinner-border-sm text-primary ms-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {buyMessage && (
        <div className={`alert ${buyMessage.type === "success" ? "alert-success" : "alert-danger"}`} role="alert">
          {buyMessage.text}
        </div>
      )}

      {/* Main Table Card */}
      <div className="opay-charge-table-card">
        <div className="table-responsive">
          <table className="table opay-charge-table">
            <thead>
              <tr>
                <th className="text-center" style={{ width: "40px" }}>
                  <input type="checkbox" className="custom-table-checkbox" />
                </th>
                <th className="text-start">Service Name</th>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Price</th>
                <th>GST</th>
                <th>Domain Renewal</th>
                <th>SSL</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {chargeList.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="text-center text-muted">
                    No charges found.
                  </td>
                </tr>
              )}
              {chargeList.map((item) => (
                <tr key={item.id}>
                  <td className="text-center">
                    <input type="checkbox" className="custom-table-checkbox" />
                  </td>
                  <td className="text-start fw-bold">
                    <span className="opay-badge opay-badge-purple">{item.service_name}</span>
                  </td>
                  <td>
                    {item.quantity_applicable ? (
                      <input
                        type="number"
                        className="opay-qty-input"
                        min={item.quantity?.min || 1}
                        max={item.quantity?.max || 99}
                        value={quantities[item.id] ?? item.quantity?.default ?? 1}
                        onChange={(e) => handleQtyChange(item.id, e.target.value)}
                        style={{ width: 70 }}
                      />
                    ) : (
                      item.quantity?.default ?? "-"
                    )}
                    {item.quantity?.min != null && item.quantity?.max != null
                      ? ` (min ${item.quantity.min} / max ${item.quantity.max})`
                      : ""}
                  </td>
                  <td>{item.unit?.type}</td>
                  <td>{item.amount?.price}</td>
                  <td>{item.amount?.gst}%</td>
                  <td>{String(item.domain_renewal) === "true" ? "Yes" : "No"}</td>
                  <td>{String(item.ssl) === "true" ? "Yes" : "No"}</td>
                  <td>
                    <button
                      className="opay-icon-btn"
                      disabled={buyingId === item.id}
                      onClick={() => handleBuyClick(item)}
                    >
                      {buyingId === item.id ? "…" : "Buy"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmItem && (
        <div className="opay-modal-overlay" onClick={closeConfirm}>
          <div className="opay-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="opay-modal-close-btn" onClick={closeConfirm} disabled={!!buyingId}>
              &times;
            </button>
            <h4 className="fw-bold mb-3">Confirm Purchase</h4>

            <div className="opay-confirm-row">
              <span>Service</span>
              <span className="opay-badge opay-badge-purple">{confirmItem.service_name}</span>
            </div>
            {confirmItem.quantity_applicable && (
              <div className="opay-confirm-row">
                <span>Quantity</span>
                <span>{qty} {confirmItem.unit?.type || ""}</span>
              </div>
            )}
            <div className="opay-confirm-row">
              <span>Unit Price</span>
              <span>₹{unitPrice.toFixed(2)}</span>
            </div>
            <div className="opay-confirm-row">
              <span>Subtotal</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="opay-confirm-row">
              <span>GST ({gstPct}%)</span>
              <span>₹{gstAmount.toFixed(2)}</span>
            </div>
            <div className="opay-confirm-row opay-confirm-total">
              <span>Total Payable</span>
              <span>₹{total.toFixed(2)}</span>
            </div>

            {buyMessage?.type === "error" && buyMessage.id === confirmItem.id && (
              <div className="alert alert-danger py-2 mt-2">{buyMessage.text}</div>
            )}

            <div className="opay-confirm-actions">
              <button className="btn btn-light" onClick={closeConfirm} disabled={!!buyingId}>
                Cancel
              </button>
              <button className="opay-pay-btn" onClick={handleConfirmBuy} disabled={!!buyingId}>
                {buyingId === confirmItem.id ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span> Processing…
                  </>
                ) : (
                  `Confirm & Pay ₹${total.toFixed(2)}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherChargeList;